import { uniq } from 'lodash-es';
import { ELEMENT_TYPES } from 'Editor/services/consts';

type DataPath = Editor.Selection.Path;

type BlockElement = Editor.Data.Node.Data;

function concatRegex(...exps: RegExp[]): RegExp {
  const source = exps.reduce((thisSource, regex) => {
    return thisSource + regex.source;
  }, '');
  const flags = [
    ...new Set(
      exps.reduce((thisFlags, regex) => {
        return thisFlags + regex.flags;
      }, ''),
    ),
  ].join(' ');
  return new RegExp(source, flags);
}

const alphabets = /([A-Za-z])/g;
const digits = /(\d)/g;
const prefixes = /(Mr|St|Mrs|Ms|Dr|Prof|Capt|Cpt|Lt|Mt)[.]/g;
const suffixes = /(Inc|Ltd|Jr|Sr|Co)/g;
const starters = new RegExp(
  '(Mr|Mrs|Ms|Dr|He\\s|She\\s|It\\s|They\\s|Their\\s|Our\\s|' +
    'We\\s|But\\s|However\\s|That\\s|This\\s|Wherever)',
  'g',
);
const acronyms = /([A-Z][.][A-Z][.](?:[A-Z][.])?)/g;
const websites = /[.](com|net|org|io|gov|me|edu)/g;

const IGNORE_ELEMENT_CONTENT_EXTENDED: Record<string, boolean> = {
  [ELEMENT_TYPES.NoteElement]: true,
  [ELEMENT_TYPES.PageBreakElement]: true,
  [ELEMENT_TYPES.SectionBreakElement]: true,
  [ELEMENT_TYPES.TableOfContentsElement]: true,
  [ELEMENT_TYPES.ListOfFiguresElement]: true,
  [ELEMENT_TYPES.ListOfTablesElement]: true,
  [ELEMENT_TYPES.KeywordsElement]: true,
  [ELEMENT_TYPES.AuthorsElement]: true,
  [ELEMENT_TYPES.SymbolElement]: true,
  [ELEMENT_TYPES.EquationElement]: true,
  [ELEMENT_TYPES.TrackDeleteElement]: true,
};

export class Text {
  text: string;
  private _parsedText: string | null;
  private _words: string[] | null;

  constructor(text: string) {
    this.text = text;
    this._parsedText = null;
    this._words = null;
  }

  static collectInfoFromBlockExtended(element: BlockElement, dataPath: DataPath): string[] {
    let content: string[] = [];
    if (!IGNORE_ELEMENT_CONTENT_EXTENDED[element.type]) {
      if (element.childNodes) {
        for (let i = 0; i < element.childNodes.length; i++) {
          const child = element.childNodes[i];
          const result = Text.collectInfoFromBlockExtended(child, [...dataPath, 'childNodes', i]);
          content = [...content, ...result];
        }
      }
      if (element.type === 'text' && element.content) {
        for (let j = 0; j < element.content.length; j++) {
          content.push(element.content[j]);
        }
      }
    }
    return content;
  }

  static textFromBlockExtended(data: BlockElement, thisPath: DataPath = []): Text {
    const content = Text.collectInfoFromBlockExtended({ ...data }, [...thisPath]);
    return new Text(content.join(''));
  }

  get textNoSpace() {
    if (this.text) {
      return this.text.replace(/\s/g, '');
    }
    return '';
  }

  get allWords() {
    if (this._words == null) {
      this._words = this.parsedText
        .replace(/(["”,.?!:()]+)/g, '')
        .replace(/<prd>/g, '.')
        .split(/ +/g)
        .slice(1, -1);
    }
    return this._words;
  }

  get parsedText() {
    if (this._parsedText === null) {
      this._parsedText = ` ${this.text}  `
        .replace('\n', ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(prefixes, '$1<prd>')
        .replace(websites, '<prd>$1')
        .replace(/Ph[.]D[.]/g, 'Ph<prd>D<prd>')
        .replace(concatRegex(/\s/, alphabets, /[.] /), ' $1<prd> ')
        .replace(concatRegex(acronyms, / /, starters), '$1<stop> $2')
        .replace(
          concatRegex(alphabets, /[.]/, alphabets, /[.]/, alphabets, /[.]/),
          '$1<prd>$2<prd>$3<prd>',
        )
        .replace(concatRegex(alphabets, /[.]/, alphabets, /[.]/), '$1<prd>$2<prd>')
        .replace(concatRegex(/ /, suffixes, /[.] /, starters), ' $1<stop> $2')
        .replace(concatRegex(/ /, suffixes, /[.]/), ' $1<prd>')
        .replace(concatRegex(/ /, alphabets, /[.]/), ' $1<prd>')
        .replace(concatRegex(digits, /[.]/, digits), '$1<prd>$2')
        .replace(/e[.]g[.]/g, 'e<prd>g<prd>')
        .replace(/i[.]e[.]/g, 'i<prd>e<prd>');
    }
    return this._parsedText;
  }

  get words() {
    if (this._words == null) {
      this._words = uniq(
        this.parsedText
          .replace(/(["”,.?!:()]+)/g, '')
          .replace(/<prd>/g, '.')
          .split(/ +/g)
          .slice(1, -1),
      );
    }
    return this._words;
  }
}
